import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Check, Loader, Eraser, ShieldCheck } from 'lucide-react';
import { StageWrap, BackHeader, PrimaryButton } from './BookingApp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_LABEL = 'text-[10px] font-bold uppercase tracking-[0.25em] text-[#d97736] mb-3';

const HEALTH_ITEMS = [
  { key: 'pregnant', label: 'I am pregnant' },
  { key: 'heart_or_bp', label: 'I have heart conditions or high blood pressure' },
  { key: 'heat_sensitivity', label: 'I have heat sensitivity or intolerance' },
  { key: 'medical_condition', label: 'I have a medical condition requiring physician supervision' },
  { key: 'under_medical_care', label: 'I am currently under medical care' },
  { key: 'none_of_above', label: 'None of the above' },
];

const BigCheckbox = ({ testId, checked, onChange, children }) => (
  <label
    data-testid={testId}
    className={`flex items-start gap-3 rounded-2xl border px-4 py-3.5 cursor-pointer transition-colors ${
      checked ? 'border-[#1f3d2b] bg-[#1f3d2b]/5' : 'border-gray-200 bg-white'
    }`}
  >
    <span className={`mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
      checked ? 'bg-[#1f3d2b] border-[#1f3d2b]' : 'border-gray-300'
    }`}>
      {checked && <Check size={14} className="text-white" strokeWidth={3} />}
    </span>
    <input type="checkbox" className="hidden" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span className="text-sm text-[#0d1f15] leading-snug">{children}</span>
  </label>
);

const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.clientWidth * ratio;
    canvas.height = canvas.clientHeight * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = '#0d1f15';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = getPos(e);
    canvasRef.current.getContext('2d').beginPath();
    canvasRef.current.getContext('2d').moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
    onChange(canvasRef.current.toDataURL('image/png'));
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange('');
  };

  return (
    <div>
      <div className="relative rounded-2xl border-2 border-dashed border-gray-300 bg-white overflow-hidden" style={{ height: 150 }}>
        <canvas
          data-testid="waiver-signature-canvas"
          ref={canvasRef}
          className="w-full h-full touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!hasSignature && (
          <div
            className="absolute inset-0 flex items-center justify-center text-gray-300 text-lg pointer-events-none"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Sign here
          </div>
        )}
      </div>
      <button
        data-testid="waiver-signature-clear"
        type="button"
        onClick={clear}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#5a7062] hover:text-[#1f3d2b] transition-colors"
      >
        <Eraser size={13} /> Clear
      </button>
    </div>
  );
};

const IntakeForm = ({ defaults, onBack, onSuccess }) => {
  const [fullName, setFullName] = useState(defaults?.firstName || defaults?.fullName || '');
  const [phone, setPhone] = useState(defaults?.phone || '');
  const [email, setEmail] = useState(defaults?.email || '');
  const [health, setHealth] = useState({
    pregnant: false, heart_or_bp: false, heat_sensitivity: false,
    medical_condition: false, under_medical_care: false, none_of_above: false,
  });
  const [safetyAck, setSafetyAck] = useState(false);
  const [liabilityAck, setLiabilityAck] = useState(false);
  const [usageAck, setUsageAck] = useState(false);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const today = new Date().toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

  const toggleHealth = (key, val) => {
    setHealth((h) => {
      if (key === 'none_of_above' && val) {
        return { pregnant: false, heart_or_bp: false, heat_sensitivity: false, medical_condition: false, under_medical_care: false, none_of_above: true };
      }
      return { ...h, [key]: val, none_of_above: key === 'none_of_above' ? val : false };
    });
  };

  const healthAnswered = Object.values(health).some(Boolean);
  const canSubmit = fullName.trim() && phone.trim() && /^[^@]+@[^@]+\.[^@]+$/.test(email.trim())
    && healthAnswered && safetyAck && liabilityAck && usageAck && !!signature;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/waivers/submit`, {
        full_name: fullName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        health_screening: health,
        safety_ack: safetyAck,
        liability_ack: liabilityAck,
        usage_rules_ack: usageAck,
        signature,
      });
      await onSuccess({ firstName: fullName.trim(), phone: phone.trim(), email: email.trim() });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save the intake form. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <StageWrap k="waiver">
      <div className="px-4 pt-6 flex-1 flex flex-col">
        <BackHeader onBack={onBack} title="Intake Form" />

        <div className="text-center mb-6">
          <ShieldCheck className="mx-auto text-[#1f3d2b] mb-2" size={26} strokeWidth={1.6} />
          <h1 className="text-2xl text-[#0d1f15] leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Wellness Acknowledgment &amp; Intake Form
          </h1>
          <p className="text-xs text-[#5a7062] mt-1">Quick — takes under 30 seconds. Required before your first session.</p>
        </div>

        <div className="mb-6">
          <div className={SECTION_LABEL}>Basic Info</div>
          <div className="space-y-3">
            <input
              data-testid="waiver-full-name" value={fullName} onChange={(e) => setFullName(e.target.value)}
              placeholder="Full name" className="w-full h-14 px-4 text-base bg-white border border-gray-300 rounded-2xl focus:outline-none focus:border-[#1f3d2b] focus:ring-2 focus:ring-[#1f3d2b]/15 transition"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            />
            <input
              data-testid="waiver-phone" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel"
              placeholder="Phone number" className="w-full h-14 px-4 text-base bg-white border border-gray-300 rounded-2xl focus:outline-none focus:border-[#1f3d2b] focus:ring-2 focus:ring-[#1f3d2b]/15 transition"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            />
            <input
              data-testid="waiver-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              placeholder="Email" className="w-full h-14 px-4 text-base bg-white border border-gray-300 rounded-2xl focus:outline-none focus:border-[#1f3d2b] focus:ring-2 focus:ring-[#1f3d2b]/15 transition"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            />
          </div>
        </div>

        <div className="mb-6">
          <div className={SECTION_LABEL}>Health Screening</div>
          <p className="text-xs text-[#5a7062] mb-3">Please check any that apply:</p>
          <div className="space-y-2">
            {HEALTH_ITEMS.map((item) => (
              <BigCheckbox key={item.key} testId={`waiver-health-${item.key}`} checked={health[item.key]} onChange={(v) => toggleHealth(item.key, v)}>
                {item.label}
              </BigCheckbox>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <div className={SECTION_LABEL}>Safety Acknowledgment</div>
          <BigCheckbox testId="waiver-safety-ack" checked={safetyAck} onChange={setSafetyAck}>
            I understand that this session includes infrared heat, red light therapy, and massage equipment. I confirm that I am physically able to participate.
          </BigCheckbox>
        </div>

        <div className="mb-6">
          <div className={SECTION_LABEL}>Liability Waiver</div>
          <p className="text-xs text-[#3a4a40] leading-relaxed mb-3 bg-[#1f3d2b]/5 border border-[#1f3d2b]/10 rounded-xl p-3.5">
            I voluntarily agree to participate in the Wellness Club session. I understand that the use of sauna, red light therapy, and massage equipment involves inherent risks including heat exposure, dizziness, and physical discomfort. I assume full responsibility for my participation and release the business from liability for any injury or adverse effects.
          </p>
          <BigCheckbox testId="waiver-liability-ack" checked={liabilityAck} onChange={setLiabilityAck}>
            I agree to the terms above
          </BigCheckbox>
        </div>

        <div className="mb-6">
          <div className={SECTION_LABEL}>Usage Rules</div>
          <ul className="text-xs text-[#3a4a40] space-y-1.5 mb-3 list-disc list-inside">
            <li><span className="font-semibold text-[#a85820]">Remove all jewelry</span> — rings, bracelets, and earrings — before using equipment</li>
            <li>Stay hydrated before and after session</li>
            <li>Exit immediately if you feel dizzy or unwell</li>
            <li>Limit sauna use to recommended time</li>
            <li>Follow all instructions provided</li>
          </ul>
          <BigCheckbox testId="waiver-usage-ack" checked={usageAck} onChange={setUsageAck}>
            I agree to follow all safety guidelines
          </BigCheckbox>
        </div>

        <div className="mb-4">
          <div className={SECTION_LABEL}>Signature</div>
          <SignaturePad onChange={setSignature} />
          <p className="text-xs text-[#5a7062] mt-2">Date: {today}</p>
        </div>

        {error && (
          <div data-testid="waiver-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-2">
            {error}
          </div>
        )}
      </div>

      <div className="px-4 pb-8 pt-2">
        <PrimaryButton dataTestid="waiver-submit-button" disabled={!canSubmit || submitting} onClick={handleSubmit}>
          {submitting
            ? <span className="inline-flex items-center gap-2"><Loader className="animate-spin" size={18} /> Saving…</span>
            : 'Agree & Continue'}
        </PrimaryButton>
      </div>
    </StageWrap>
  );
};

export default IntakeForm;
